import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import Loader from '../../components/Loader';
import api from '../../api';
import { useAdmin } from '../../context/AdminContext';

const typeOptions   = ['', 'flight', 'visa', 'hotel', 'holiday', 'general'];
const statusOptions = ['new', 'contacted', 'in-progress', 'closed'];
const statusColors  = { new: '#3F72AF', contacted: '#4c7a52', 'in-progress': '#e07b1a', closed: '#888' };

export default function AdminInquiries() {
  const [inquiries, setInquiries]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterType, setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected]       = useState(null);
  const [checked, setChecked]         = useState([]);
  const [deleting, setDeleting]       = useState(false);
  const { isSuperAdmin } = useAdmin();

  function load() {
    setLoading(true);
    setChecked([]);
    var params = new URLSearchParams();
    if (filterType)   params.set('type', filterType);
    if (filterStatus) params.set('status', filterStatus);
    api.get('/inquiries?' + params.toString())
      .then(function(res) { setInquiries(res.data.inquiries); })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }

  useEffect(function() { load(); }, [filterType, filterStatus]); // eslint-disable-line

  function toggleCheck(id) {
    setChecked(function(prev) {
      return prev.includes(id) ? prev.filter(function(x) { return x !== id; }) : [...prev, id];
    });
  }

  function toggleAll() {
    if (checked.length === inquiries.length) {
      setChecked([]);
    } else {
      setChecked(inquiries.map(function(i) { return i._id; }));
    }
  }

  async function updateStatus(id, status) {
    await api.put('/inquiries/' + id, { status });
    load();
  }

  async function deleteSingle(id) {
    if (!window.confirm('Delete this inquiry?')) return;
    await api.delete('/inquiries/' + id);
    setSelected(null);
    load();
  }

  async function deleteSelected() {
    if (!checked.length) return;
    if (!window.confirm('Delete ' + checked.length + ' selected inquiries? This cannot be undone.')) return;
    setDeleting(true);
    await Promise.all(checked.map(function(id) { return api.delete('/inquiries/' + id); }));
    setDeleting(false);
    load();
  }

  async function deleteAll() {
    if (!window.confirm('Delete ALL ' + inquiries.length + ' inquiries currently shown? This cannot be undone.')) return;
    setDeleting(true);
    await Promise.all(inquiries.map(function(i) { return api.delete('/inquiries/' + i._id); }));
    setDeleting(false);
    load();
  }

  var allChecked  = inquiries.length > 0 && checked.length === inquiries.length;
  var someChecked = checked.length > 0;

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 'clamp(1.4rem, 4vw, 2rem)' }}>Inquiries</h1>

        {/* Bulk action bar */}
        {someChecked && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bj-cream-soft)', padding: '8px 14px', borderRadius: 8, border: '1.5px solid var(--bj-sand)' }}>
            <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{checked.length} selected</span>
            {isSuperAdmin() && ( <button
              className="btn btn-outline"
              style={{ padding: '5px 12px', fontSize: '0.82rem', borderColor: '#ef4444', color: '#ef4444' }}
              onClick={deleteSelected}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete Selected'}
            </button>
            )}
            <button
              className="btn btn-outline"
              style={{ padding: '5px 12px', fontSize: '0.82rem' }}
              onClick={function() { setChecked([]); }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={filterType} onChange={function(e) { setFilterType(e.target.value); }}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1.5px solid var(--bj-sand)', fontFamily: 'var(--font-body)', flex: 1, minWidth: 120 }}>
          {typeOptions.map(function(t) { return <option key={t} value={t}>{t ? t[0].toUpperCase() + t.slice(1) : 'All Types'}</option>; })}
        </select>
        <select value={filterStatus} onChange={function(e) { setFilterStatus(e.target.value); }}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1.5px solid var(--bj-sand)', fontFamily: 'var(--font-body)', flex: 1, minWidth: 120 }}>
          <option value="">All Statuses</option>
          {statusOptions.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
        </select>
        <button className="btn btn-outline" onClick={load} style={{ padding: '8px 14px', flexShrink: 0 }}>↻</button>
        {inquiries.length > 0 && isSuperAdmin() && (
          <button
            className="btn btn-outline"
            style={{ padding: '8px 14px', fontSize: '0.82rem', borderColor: '#ef4444', color: '#ef4444', flexShrink: 0 }}
            onClick={deleteAll}
            disabled={deleting}
          >
            🗑 Delete All ({inquiries.length})
          </button>
        )}
      </div>

      {loading ? <Loader /> : (
        <>
          {/* Mobile card list */}
          <div className="mobile-only" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {inquiries.length === 0 && <p>No inquiries match these filters.</p>}
            {inquiries.map(function(inq) {
              return (
                <div key={inq._id} className="card" style={{ padding: 16, borderLeft: checked.includes(inq._id) ? '3px solid var(--bj-clay)' : '3px solid transparent' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                    <input type="checkbox" checked={checked.includes(inq._id)} onChange={function() { toggleCheck(inq._id); }} style={{ marginTop: 4, accentColor: 'var(--bj-clay)', width: 16, height: 16, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 700 }}>{inq.name}</p>
                      <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.6 }}>{inq.phone} · {inq.email}</p>
                    </div>
                    <span style={{ background: (statusColors[inq.status] || '#888') + '20', color: statusColors[inq.status] || '#888', padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize', flexShrink: 0 }}>
                      {inq.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ background: 'var(--bj-clay)18', color: 'var(--bj-clay)', padding: '2px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize' }}>{inq.type}</span>
                    <select value={inq.status} onChange={function(e) { updateStatus(inq._id, e.target.value); }}
                      style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1.5px solid var(--bj-sand)', fontFamily: 'var(--font-body)', fontSize: '0.82rem' }}>
                      {statusOptions.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button className="btn btn-outline" style={{ flex: 1, padding: '6px 0', fontSize: '0.82rem', justifyContent: 'center' }} onClick={function() { setSelected(inq); }}>View</button>
                    {isSuperAdmin() && (<button className="btn btn-outline" style={{ flex: 1, padding: '6px 0', fontSize: '0.82rem', justifyContent: 'center', borderColor: '#ef4444', color: '#ef4444' }} onClick={function() { deleteSingle(inq._id); }}>Delete</button> )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="card table-wrap desktop-only" style={{ display: 'block' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ background: 'var(--bj-cream-soft)', textAlign: 'left' }}>
                  <th style={{ padding: 12, width: 40 }}>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                      style={{ accentColor: 'var(--bj-clay)', width: 16, height: 16 }}
                      title="Select all"
                    />
                  </th>
                  {['Name', 'Type', 'Phone', 'Email', 'Status', 'Received', ''].map(function(h) {
                    return <th key={h} style={{ padding: 12, fontSize: '0.83rem', fontWeight: 700 }}>{h}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {inquiries.map(function(inq) {
                  var isChecked = checked.includes(inq._id);
                  return (
                    <tr key={inq._id} style={{ borderTop: '1px solid var(--bj-sand)', background: isChecked ? 'rgba(63,114,175,0.04)' : 'transparent' }}>
                      <td style={{ padding: '12px 12px' }}>
                        <input type="checkbox" checked={isChecked} onChange={function() { toggleCheck(inq._id); }}
                          style={{ accentColor: 'var(--bj-clay)', width: 16, height: 16 }} />
                      </td>
                      <td style={{ padding: 12 }}>{inq.name}</td>
                      <td style={{ padding: 12, textTransform: 'capitalize' }}>{inq.type}</td>
                      <td style={{ padding: 12 }}>{inq.phone}</td>
                      <td style={{ padding: 12 }}>{inq.email}</td>
                      <td style={{ padding: 12 }}>
                        <select value={inq.status} onChange={function(e) { updateStatus(inq._id, e.target.value); }}
                          style={{ padding: '4px 8px', borderRadius: 6, border: '1.5px solid var(--bj-sand)', fontFamily: 'var(--font-body)' }}>
                          {statusOptions.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
                        </select>
                      </td>
                      <td style={{ padding: 12, fontSize: '0.8rem', opacity: 0.6 }}>{new Date(inq.createdAt).toLocaleDateString()}</td>
                      <td style={{ padding: 12 }}>
                        <button className="btn btn-outline" style={{ padding: '4px 10px', marginRight: 6, fontSize: '0.82rem' }} onClick={function() { setSelected(inq); }}>View</button>
                        {isSuperAdmin() && (<button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.82rem', borderColor: '#ef4444', color: '#ef4444' }} onClick={function() { deleteSingle(inq._id); }}>Delete</button>)}
                      </td>
                    </tr>
                  );
                })}
                {inquiries.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 20, opacity: 0.6 }}>No inquiries match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Detail modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }}
          onClick={function() { setSelected(null); }}>
          <div className="card" style={{ padding: 24, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto' }}
            onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, textTransform: 'capitalize' }}>{selected.type} Inquiry</h3>
              <button onClick={function() { setSelected(null); }} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.82rem', background: 'var(--bj-cream-soft)', padding: 14, borderRadius: 8, overflowX: 'auto' }}>
              {JSON.stringify(selected, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}